import React, { useState, useEffect } from 'react';
import { itemsApi, imagesApi, aiApi } from '../../utils/api';
import { Spinner } from '../shared/Spinner';
import { AddImageButton } from '../shared/AddImageButton';

interface ItemCreateFormProps {
  projectId?: string;
  onSuccess: (item: any) => void;
  onCancel: () => void;
}

export const ItemCreateForm: React.FC<ItemCreateFormProps> = ({ projectId, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    condition: 'used' as 'new' | 'used' | 'salvage',
    price: '',
    currency: 'NZD',
    materials: [] as string[],
    dimensions: {
      width: '',
      height: '',
      depth: '',
      units: 'cm' as 'mm' | 'cm' | 'm' | 'in' | 'ft'
    }
  });
  
  const [images, setImages] = useState<Array<{id: string, url: string, filename: string}>>([]);
  const [showImageSelector, setShowImageSelector] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiEstimation, setAiEstimation] = useState<any>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleImageAdded = () => {
    setShowImageSelector(true);
  };

  const handleSelectImages = async () => {
    setShowImageSelector(true);
  };

  const handleImageSelect = async (photo: any) => {
    const imageData = {
      id: photo.id,
      url: photo.url,
      filename: photo.filename
    };
    
    // Add to selected images if not already selected
    if (!images.find(img => img.id === photo.id)) {
      const newImages = [...images, imageData];
      setImages(newImages);
      
      // Automatically trigger AI analysis with the first image
      if (newImages.length === 1) {
        await handleAiAnalysis(newImages);
      }
    }
  };

  const handleAiAnalysis = async (imageList: typeof images) => {
    if (imageList.length === 0) return;

    setAiProcessing(true);
    setErrors({});

    try {
      // Extract base64 data from data URL
      const imageUrl = imageList[0].url;
      let requestData: any = {
        title: '',
        description: '',
        condition: 'used',
        materials: [],
        dimensions: {
          width: 0,
          height: 0,
          depth: 0,
          units: 'cm'
        }
      };

      // Check if it's a base64 data URL or regular URL
      if (imageUrl.startsWith('data:')) {
        // Extract base64 data from data URL
        const base64Data = imageUrl.split(',')[1];
        requestData.base64Image = base64Data;
      } else {
        // It's a regular URL
        requestData.imageUrl = imageUrl;
      }

      const response = await aiApi.estimate(requestData);

      if (response.data.success && response.data.data) {
        const estimation = response.data.data;
        
        // Auto-fill all form fields from AI
        setFormData({
          title: estimation.recommendedTitle || 'Untitled Item',
          description: estimation.notes || '',
          condition: 'used', // AI doesn't return condition, default to used
          price: estimation.estimatedValue?.toString() || '0',
          currency: 'NZD',
          materials: estimation.attributes || [],
          dimensions: {
            width: '',
            height: '',
            depth: '',
            units: 'cm' as const
          }
        });

        // Store AI estimation - this ensures both price and aiAnalysis.estimatedValue start the same
        setAiEstimation({
          ...estimation,
          estimatedValue: estimation.estimatedValue || 0 // Ensure we have a value
        });
      }
    } catch (err: any) {
      const message = err.response?.data?.error?.message || 'AI analysis failed';
      setErrors({ ai: message });
    } finally {
      setAiProcessing(false);
    }
  };

  const handleRemoveImage = (indexToRemove: number) => {
    setImages(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (images.length === 0) {
      setErrors({ images: 'At least one image is required' });
      return;
    }

    if (!formData.title.trim()) {
      setErrors({ title: 'Title is required' });
      return;
    }

    const price = parseFloat(formData.price);
    if (isNaN(price) || price < 0) {
      setErrors({ price: 'Please enter a valid price' });
      return;
    }

    setLoading(true);
    try {
      const itemData = {
        title: formData.title,
        description: formData.description,
        images,
        condition: formData.condition,
        price,
        currency: formData.currency,
        materials: formData.materials,
        dimensions: formData.dimensions.width ? {
          width: parseFloat(formData.dimensions.width),
          height: parseFloat(formData.dimensions.height),
          depth: parseFloat(formData.dimensions.depth),
          units: formData.dimensions.units
        } : undefined,
        project: projectId,
        // Include AI analysis if available
        ...(aiEstimation && {
          aiAnalysis: {
            estimatedValue: aiEstimation.estimatedValue,
            confidence: aiEstimation.confidence,
            notes: aiEstimation.notes,
            recommendedTitle: aiEstimation.recommendedTitle,
            attributes: aiEstimation.attributes,
            reasoning: aiEstimation.reasoning
          }
        })
      };

      const response = await itemsApi.create(itemData);

      if (response.data.success) {
        onSuccess(response.data.data);
      } else {
        throw new Error(response.data.error?.message || 'Failed to create item');
      }
    } catch (err: any) {
      const message = err.response?.data?.error?.message || 'Failed to create item';
      setErrors({ submit: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-2xl font-bold">Add New Item</h2>
          <button
            onClick={onCancel}
            className="btn-ghost p-2"
            disabled={loading}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[70vh]">
        <form onSubmit={handleSubmit} className="space-y-6">
              {/* Images */}
              <div className="form-group">
                <label className="form-label">
                  Images *
                </label>
                
              <div className="space-y-4">
                <div className="flex space-x-2">
                  <AddImageButton onImageAdded={handleImageAdded} />
                  <button
                    type="button"
                    onClick={handleSelectImages}
                    className="btn-secondary"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    Select from Photos
                  </button>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Upload new images or select from your existing photos. AI will automatically analyze the first image.
                  </p>
                </div>

                {/* Image Preview */}
                {images.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                  {images.map((image, index) => (
                    <div key={image.id} className="relative group">
                        <img
                        src={image.url}
                        alt={image.filename}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {errors.images && <p className="form-error">{errors.images}</p>}
              </div>

            {/* AI Processing Status */}
            {aiProcessing && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center">
                  <Spinner size="sm" className="mr-3" />
                  <div>
                    <h3 className="font-medium text-blue-900 dark:text-blue-100">Analyzing Images...</h3>
                    <p className="text-sm text-blue-700 dark:text-blue-300">AI is identifying the item and generating details</p>
              </div>
              </div>
            </div>
            )}

            {/* AI Results */}
            {aiEstimation && !aiProcessing && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                <h3 className="font-medium text-green-900 dark:text-green-100 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  AI Analysis Complete - Ready to Create Item
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-slate-100">Title</h4>
                    <p className="text-slate-700 dark:text-slate-300">{formData.title}</p>
                </div>

                  {formData.description && (
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-slate-100">Description</h4>
                      <p className="text-slate-700 dark:text-slate-300">{formData.description}</p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Pricing Information</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-blue-700 dark:text-blue-300">AI Estimated Value:</span>
                          <span className="font-semibold text-blue-600 dark:text-blue-400">
                            ${aiEstimation.estimatedValue?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-blue-700 dark:text-blue-300">Your Listing Price:</span>
                          <span className="font-semibold text-green-600 dark:text-green-400">
                            ${formData.price}
                          </span>
                        </div>
                        {parseFloat(formData.price) !== aiEstimation.estimatedValue && (
                          <div className="flex justify-between items-center pt-2 border-t border-blue-200 dark:border-blue-700">
                            <span className="text-sm text-blue-700 dark:text-blue-300">Difference:</span>
                            <span className={`font-medium text-sm ${
                              parseFloat(formData.price) > aiEstimation.estimatedValue 
                                ? 'text-red-600 dark:text-red-400' 
                                : 'text-orange-600 dark:text-orange-400'
                            }`}>
                              {parseFloat(formData.price) > aiEstimation.estimatedValue ? '+' : ''}${(parseFloat(formData.price) - aiEstimation.estimatedValue).toFixed(2)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-blue-700 dark:text-blue-300">AI Confidence:</span>
                          <span className="text-sm text-blue-600 dark:text-blue-400">
                            {(aiEstimation.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div className="mt-3 pt-2 border-t border-blue-200 dark:border-blue-700">
                          <p className="text-xs text-blue-600 dark:text-blue-400">
                            💡 The AI estimated value is preserved for reference. You can edit the listing price in the form below.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-slate-100">Condition</h4>
                      <p className="text-slate-700 dark:text-slate-300 capitalize">{formData.condition}</p>
                    </div>
                  </div>

                {formData.materials.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-slate-100">Materials</h4>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {formData.materials.map((material, index) => (
                          <span key={index} className="px-2 py-1 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded text-sm">
                        {material}
                      </span>
                    ))}
                      </div>
                  </div>
                )}
              </div>
                        </div>
                      )}
                      
            {errors.ai && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-700 dark:text-red-300">{errors.ai}</p>
                  </div>
                )}

          {errors.submit && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-700 dark:text-red-300">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary flex-1"
                disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1 flex items-center justify-center"
                disabled={loading || !formData.title.trim() || images.length === 0}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Creating...
                </>
              ) : (
                'Create Item'
              )}
            </button>
          </div>
        </form>
        </div>

        {/* Image Selector Modal */}
        {showImageSelector && <ImageSelectorModal />}
      </div>
    </div>
  );

  function ImageSelectorModal() {
    const [availablePhotos, setAvailablePhotos] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      loadPhotos();
    }, []);

    const loadPhotos = async () => {
      try {
        const response = await imagesApi.list();
        if (response.data.success) {
          setAvailablePhotos(response.data.photos);
        }
      } catch (error) {
        console.error('Failed to load photos:', error);
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              Select Images
            </h2>
            <button
              onClick={() => setShowImageSelector(false)}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Spinner />
              </div>
            ) : availablePhotos.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-500 dark:text-slate-400">No photos available. Upload some photos first.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {availablePhotos.map((photo) => {
                  const isSelected = images.find(img => img.id === photo.id);
                  return (
                    <div
                      key={photo.id}
                      className={`relative cursor-pointer rounded-lg overflow-hidden transition-all ${
                        isSelected 
                          ? 'ring-2 ring-primary-500 ring-offset-2' 
                          : 'hover:shadow-lg'
                      }`}
                      onClick={() => handleImageSelect(photo)}
                    >
                      <div className="aspect-square bg-slate-200 dark:bg-slate-700">
                        <img
                          src={photo.url}
                          alt={photo.filename}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <p className="text-white text-xs truncate">{photo.filename}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-2 p-6 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setShowImageSelector(false)}
              className="btn-secondary"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }
};